import React, { useState } from 'react';
import { 
  Building2, 
  X, 
  Sparkles, 
  Check, 
  Copy, 
  Briefcase, 
  Layers, 
  ShieldCheck, 
  MapPin, 
  Mail, 
  FileText,
  Plus
} from 'lucide-react';
import { useAuth, VendorProfile } from '../AuthContext';
import { motion, AnimatePresence } from 'motion/react';

interface CreateVendorProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProfileCreated?: (newId: string) => void;
}

const TEMPLATE_OPTIONS = [
  {
    id: 'scratch',
    title: 'Start Fresh',
    desc: 'Create an independent corporate profile with new credentials.',
    icon: Plus
  },
  {
    id: 'clone_active',
    title: 'Duplicate Active Profile',
    desc: 'Inherit certifications, branding, and intelligence for a new DBA or subsidiary.',
    icon: Copy
  },
  {
    id: 'govcon',
    title: 'Federal / GovCon Division',
    desc: 'Pre-configured with Federal CAGE/UEI fields and standard NAICS codes.',
    icon: ShieldCheck
  }
];

export const CreateVendorProfileModal: React.FC<CreateVendorProfileModalProps> = ({
  isOpen,
  onClose,
  onProfileCreated
}) => {
  const { vendorProfile, createProfile } = useAuth();

  const [selectedTemplate, setSelectedTemplate] = useState<'scratch' | 'clone_active' | 'govcon'>('scratch');
  const [companyName, setCompanyName] = useState('');
  const [label, setLabel] = useState('Subsidiary Division');
  const [ein, setEin] = useState('');
  const [incorporationDate, setIncorporationDate] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [dunsNumber, setDunsNumber] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [businessRegisteredState, setBusinessRegisteredState] = useState('South Dakota');
  const [website, setWebsite] = useState('');
  const [naicsInput, setNaicsInput] = useState('541511, 541512');
  const [isDefault, setIsDefault] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleTemplateChange = (templateId: 'scratch' | 'clone_active' | 'govcon') => {
    setSelectedTemplate(templateId);
    if (templateId === 'clone_active' && vendorProfile) {
      setCompanyName(`${vendorProfile.companyName} (DBA)`);
      setLabel('DBA Subsidiary');
      setEin(vendorProfile.ein || '');
      setIncorporationDate(vendorProfile.incorporationDate || '');
      setCompanyId(vendorProfile.companyId || vendorProfile.dosId || '');
      setDunsNumber(vendorProfile.dunsNumber || vendorProfile.duns || '');
      setContactEmail(vendorProfile.contactEmail || '');
      setBusinessRegisteredState(vendorProfile.businessRegisteredState || 'South Dakota');
      setWebsite(vendorProfile.website || '');
      setNaicsInput(vendorProfile.naicsCodes?.join(', ') || '541511, 541512');
    } else if (templateId === 'govcon') {
      setCompanyName(vendorProfile ? `${vendorProfile.companyName} Federal LLC` : 'Federal Systems Group');
      setLabel('GovCon & Defense');
      setBusinessRegisteredState('Virginia');
      setIncorporationDate('');
      setCompanyId('');
      setDunsNumber('');
      setNaicsInput('541511, 541512, 541519, 541330');
    } else {
      setCompanyName('');
      setLabel('Secondary Entity');
      setEin('');
      setIncorporationDate('');
      setCompanyId('');
      setDunsNumber('');
      setContactEmail('');
      setBusinessRegisteredState('South Dakota');
      setWebsite('');
      setNaicsInput('541511, 541512');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyName.trim()) {
      setError('Please provide a legal company or entity name.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const parsedNaics = naicsInput.split(',').map(s => s.trim()).filter(Boolean);

      let baseProfile: Partial<VendorProfile> = {};

      if (selectedTemplate === 'clone_active' && vendorProfile) {
        // Deep copy from current active profile
        baseProfile = {
          ...vendorProfile,
          logoUrl: vendorProfile.logoUrl || '',
          colorScheme: vendorProfile.colorScheme || 'sapphire',
          mainColor: vendorProfile.mainColor || '#1e3a8a',
          bgColor: vendorProfile.bgColor || '#f8fafc',
          fontFamily: vendorProfile.fontFamily || 'Inter',
          themeMode: vendorProfile.themeMode || 'light',
          capabilities: vendorProfile.capabilities || ['(SB) Small Business'],
          designations: vendorProfile.designations || ['(SB) Small Business'],
          businessAddress: vendorProfile.businessAddress || {},
          shippingAddress: vendorProfile.shippingAddress || {},
          billToAddress: vendorProfile.billToAddress || {},
          invoicingAddress: vendorProfile.invoicingAddress || {},
          remittanceAddress: vendorProfile.remittanceAddress || {},
          websiteIntelligence: vendorProfile.websiteIntelligence,
          linkedInIntelligence: vendorProfile.linkedInIntelligence,
          partnerOnIntelligence: vendorProfile.partnerOnIntelligence,
          businessEntityData: vendorProfile.businessEntityData
        };
      }

      const newId = await createProfile({
        ...baseProfile,
        companyName: companyName.trim(),
        label: label.trim() || 'Secondary Entity',
        ein: ein.trim() || undefined,
        incorporationDate: incorporationDate.trim() || undefined,
        companyId: companyId.trim() || undefined,
        dosId: companyId.trim() || undefined,
        dunsNumber: dunsNumber.trim() || undefined,
        duns: dunsNumber.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        businessRegisteredState: businessRegisteredState.trim() || 'South Dakota',
        businessRegisteredCountry: 'United States',
        website: website.trim() || undefined,
        naicsCodes: parsedNaics.length > 0 ? parsedNaics : ['541511'],
        nigpCodes: ['920-00', '918-29'],
        capabilities: baseProfile.capabilities || ['(SB) Small Business'],
        designations: baseProfile.designations || ['(SB) Small Business'],
        isDefault: isDefault
      }, true); // true = automatically switch to this new profile

      if (onProfileCreated) {
        onProfileCreated(newId);
      }
      onClose();
    } catch (err: any) {
      console.error("Error creating vendor profile:", err);
      setError(err.message || 'Failed to create vendor profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        className="bg-white w-full max-w-xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Modal Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-md shadow-indigo-100">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Add New Vendor Profile</h2>
              <p className="text-xs text-slate-500">Manage multiple corporate divisions, DBAs, or joint ventures under one account.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            id="btn-close-create-profile-modal"
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {error && (
            <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs font-medium">
              {error}
            </div>
          )}

          {/* Template Selection */}
          <div className="space-y-2">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500">
              Creation Preset
            </label>
            <div className="grid grid-cols-3 gap-2.5">
              {TEMPLATE_OPTIONS.map(tpl => {
                const isSelected = selectedTemplate === tpl.id;
                const Icon = tpl.icon;
                return (
                  <button
                    type="button"
                    key={tpl.id}
                    onClick={() => handleTemplateChange(tpl.id as any)}
                    className={`p-3 rounded-2xl border text-left transition-all cursor-pointer flex flex-col justify-between ${
                      isSelected
                        ? 'border-indigo-600 bg-indigo-50/60 shadow-xs ring-2 ring-indigo-600/10'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className={`p-1.5 rounded-xl ${isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-indigo-600" />}
                    </div>
                    <div>
                      <p className={`text-xs font-bold ${isSelected ? 'text-indigo-900' : 'text-slate-800'}`}>{tpl.title}</p>
                      <p className="text-[10px] text-slate-500 mt-0.5 leading-tight">{tpl.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Profile Name & Label */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                Company Legal Name <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="e.g. Apex Federal Systems LLC"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/10 rounded-xl text-sm font-medium transition-all"
                id="input-create-company-name"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                Entity Label / Division Name
              </label>
              <input
                type="text"
                value={label}
                onChange={e => setLabel(e.target.value)}
                placeholder="e.g. Federal GovCon, West Coast DBA"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/10 rounded-xl text-sm font-medium transition-all"
                id="input-create-company-label"
              />
            </div>
          </div>

          {/* EIN & State */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                EIN / Tax ID
              </label>
              <input
                type="text"
                value={ein}
                onChange={e => setEin(e.target.value)}
                placeholder="XX-XXXXXXX"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/10 rounded-xl text-sm font-medium transition-all"
                id="input-create-ein"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                Registered State
              </label>
              <input
                type="text"
                value={businessRegisteredState}
                onChange={e => setBusinessRegisteredState(e.target.value)}
                placeholder="e.g. South Dakota, Virginia, Delaware"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/10 rounded-xl text-sm font-medium transition-all"
                id="input-create-state"
              />
            </div>
          </div>

          {/* Corporate Registry Identifiers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                Company ID / File #
              </label>
              <input
                type="text"
                value={companyId}
                onChange={e => setCompanyId(e.target.value)}
                placeholder="DOS-SD-482910"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/10 rounded-xl text-sm font-mono font-medium transition-all"
                id="input-create-company-id"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                Incorporation Date
              </label>
              <input
                type="date"
                value={incorporationDate}
                onChange={e => setIncorporationDate(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/10 rounded-xl text-sm font-medium transition-all"
                id="input-create-incorporation-date"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                DUNS Number
              </label>
              <input
                type="text"
                value={dunsNumber}
                onChange={e => setDunsNumber(e.target.value)}
                placeholder="08-123-4567"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/10 rounded-xl text-sm font-mono font-medium transition-all"
                id="input-create-duns-number"
              />
            </div>
          </div>

          {/* Email & Website */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                Contact Email
              </label>
              <input
                type="email"
                value={contactEmail}
                onChange={e => setContactEmail(e.target.value)}
                placeholder="proposals@apexgovcon.com"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/10 rounded-xl text-sm font-medium transition-all"
                id="input-create-email"
              />
            </div>

            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                Corporate Website
              </label>
              <input
                type="url"
                value={website}
                onChange={e => setWebsite(e.target.value)}
                placeholder="https://apexgovcon.com"
                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/10 rounded-xl text-sm font-medium transition-all"
                id="input-create-website"
              />
            </div>
          </div>

          {/* NAICS Codes */}
          <div className="space-y-1.5">
            <label className="block text-xs font-bold text-slate-700">
              Primary NAICS Codes (Comma Separated)
            </label>
            <input
              type="text"
              value={naicsInput}
              onChange={e => setNaicsInput(e.target.value)}
              placeholder="541511, 541512, 541519, 541330"
              className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white focus:border-indigo-600 focus:ring-2 focus:ring-indigo-600/10 rounded-xl text-sm font-mono font-medium transition-all"
              id="input-create-naics"
            />
            <p className="text-[11px] text-slate-400">Used by the RFX opportunity matcher and proposal auto-generator.</p>
          </div>

          {/* Set as default checkbox */}
          <div className="pt-2">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={e => setIsDefault(e.target.checked)}
                className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                id="checkbox-create-default"
              />
              <span className="text-xs font-medium text-slate-700">
                Set as primary default profile on login
              </span>
            </label>
          </div>

          {/* Modal Footer */}
          <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              id="btn-submit-create-profile"
              className="px-6 py-2.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-xl shadow-md shadow-indigo-100 transition-all flex items-center gap-2 cursor-pointer"
            >
              {isSubmitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating Entity...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create & Activate Profile
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};
